import { useMemo, useState } from "react";
import { Radar, ArrowRightLeft, ListFilter, Route, CheckCircle2, XCircle } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const TOOLS = [
  { id: "ipflow", label: "IP flow verify", icon: ArrowRightLeft },
  { id: "effective", label: "Effective security rules", icon: ListFilter },
  { id: "nexthop", label: "Next hop", icon: Route },
];

function Field({ label, children }) {
  return (
    <label className="block text-xs">
      <span className="text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function NetworkWatcher() {
  const { state } = useAzure();
  const vms = useMemo(
    () => (state?.resources || []).filter((r) => r.type === "Microsoft.Compute/virtualMachines"),
    [state]
  );
  const [tool, setTool] = useState("ipflow");
  const [vm, setVm] = useState(vms[0]?.name || "");
  const [dir, setDir] = useState("Inbound");
  const [proto, setProto] = useState("TCP");
  const [localPort, setLocalPort] = useState("22");
  const [remoteIp, setRemoteIp] = useState("52.170.0.1");
  const [remotePort, setRemotePort] = useState("51000");
  const [destIp, setDestIp] = useState("10.10.2.5");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const inputCls = "w-full px-2 py-1.5 rounded border border-border bg-background text-sm";

  const run = async () => {
    if (!vm) return;
    setBusy(true); setRes(null);
    try {
      if (tool === "ipflow") {
        const { data } = await api.post("/azure/network-watcher/ip-flow-verify",
          { vm_name: vm, direction: dir, protocol: proto, local_port: localPort, remote_ip: remoteIp, remote_port: remotePort });
        setRes({ kind: "ipflow", data });
      } else if (tool === "effective") {
        const { data } = await api.post("/azure/network-watcher/effective-rules", { vm_name: vm, direction: dir });
        setRes({ kind: "effective", data });
      } else {
        const { data } = await api.post("/azure/network-watcher/next-hop", { vm_name: vm, dest_ip: destIp });
        setRes({ kind: "nexthop", data });
      }
    } catch (e) { setRes({ kind: "error", message: apiErr(e) }); } finally { setBusy(false); }
  };

  return (
    <div data-testid="az-network-watcher">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-4">
        <Radar className="h-5 w-5 text-[#0078d4]" />
        <h2 className="font-semibold">Network Watcher</h2>
        <span className="text-xs text-muted-foreground ml-2">Diagnostics that evaluate your real NSG &amp; routing state</span>
      </div>

      <div className="flex gap-2 mb-5">
        {TOOLS.map((t) => (
          <button key={t.id} data-testid={`az-nw-tool-${t.id}`} onClick={() => { setTool(t.id); setRes(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border ${tool === t.id ? "bg-[#0078d4] text-white border-[#0078d4]" : "border-border hover:bg-accent"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-[320px_1fr] gap-6">
        <div className="space-y-3 border border-border rounded-md p-4">
          <Field label="Target virtual machine">
            <select className={inputCls} value={vm} onChange={(e) => setVm(e.target.value)} data-testid="az-nw-vm">
              {vms.map((v) => <option key={v.id} value={v.name}>{v.name} ({v.properties.os_type})</option>)}
            </select>
          </Field>

          {tool !== "nexthop" && (
            <Field label="Direction">
              <select className={inputCls} value={dir} onChange={(e) => setDir(e.target.value)} data-testid="az-nw-direction">
                <option>Inbound</option><option>Outbound</option>
              </select>
            </Field>
          )}

          {tool === "ipflow" && (
            <>
              <Field label="Protocol">
                <select className={inputCls} value={proto} onChange={(e) => setProto(e.target.value)} data-testid="az-nw-protocol">
                  <option>TCP</option><option>UDP</option>
                </select>
              </Field>
              <Field label="Local port"><input className={inputCls} value={localPort} onChange={(e) => setLocalPort(e.target.value)} data-testid="az-nw-local-port" /></Field>
              <Field label="Remote IP address"><input className={inputCls} value={remoteIp} onChange={(e) => setRemoteIp(e.target.value)} data-testid="az-nw-remote-ip" /></Field>
              <Field label="Remote port"><input className={inputCls} value={remotePort} onChange={(e) => setRemotePort(e.target.value)} data-testid="az-nw-remote-port" /></Field>
            </>
          )}

          {tool === "nexthop" && (
            <Field label="Destination IP address"><input className={inputCls} value={destIp} onChange={(e) => setDestIp(e.target.value)} data-testid="az-nw-dest-ip" /></Field>
          )}

          <Button className="w-full bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={run} disabled={busy} data-testid="az-nw-run">
            {busy ? "Checking…" : "Check"}
          </Button>
        </div>

        <div data-testid="az-nw-result">
          {!res && <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-8 text-center">Run a diagnostic to see results evaluated against your NSG and routing configuration.</div>}
          {res?.kind === "error" && <div className="rounded-md bg-red-500/10 text-red-600 px-3 py-2 text-sm">{res.message}</div>}

          {res?.kind === "ipflow" && (
            <div className={`rounded-md border p-5 ${res.data.access === "Allow" ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}>
              <div className="flex items-center gap-2 text-lg font-semibold" data-testid="az-nw-ipflow-access">
                {res.data.access === "Allow" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
                Access: {res.data.access === "Allow" ? "Allowed" : "Denied"}
              </div>
              <div className="mt-3 text-sm space-y-1.5">
                <div className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">Matched rule</span><span className="font-mono">{res.data.rule_name}</span></div>
                <div className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">Security group</span><span className="font-mono">{res.data.nsg || "—"}</span></div>
                <div className="flex justify-between py-1"><span className="text-muted-foreground">Applied at</span><span>{res.data.scope || "—"}</span></div>
              </div>
              {res.data.note && <p className="text-xs text-muted-foreground mt-2">{res.data.note}</p>}
              {res.data.access === "Deny" && <p className="text-xs text-amber-600 mt-2">Tip: add an inbound rule allowing this port/source to the matched NSG, then re-check.</p>}
            </div>
          )}

          {res?.kind === "effective" && (
            <div className="border border-border rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Scope</TableHead><TableHead>Priority</TableHead><TableHead>Name</TableHead><TableHead>Port</TableHead><TableHead>Source</TableHead><TableHead>Access</TableHead></TableRow></TableHeader>
                <TableBody>
                  {res.data.rules.map((r, i) => (
                    <TableRow key={i} data-testid={`az-nw-rule-${r.name}`}>
                      <TableCell className="text-xs">{r.scope}</TableCell>
                      <TableCell className="font-mono text-xs">{r.priority}</TableCell>
                      <TableCell className="text-sm">{r.name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.destination_port}</TableCell>
                      <TableCell className="font-mono text-xs">{r.source}</TableCell>
                      <TableCell className={`text-xs font-medium ${r.access === "Allow" ? "text-emerald-600" : "text-red-600"}`}>{r.access}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {res?.kind === "nexthop" && (
            <div className="rounded-md border border-border p-5">
              <div className="text-lg font-semibold" data-testid="az-nw-nexthop-type">Next hop type: {res.data.next_hop_type}</div>
              <div className="mt-3 text-sm space-y-1.5">
                <div className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">Next hop IP</span><span className="font-mono">{res.data.next_hop_ip || "—"}</span></div>
                <div className="flex justify-between py-1"><span className="text-muted-foreground">Route</span><span className="font-mono">{res.data.route_name || "—"}</span></div>
              </div>
              {res.data.note && <p className="text-xs text-muted-foreground mt-2">{res.data.note}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
